import { useState } from 'react';

/**
 * Custom hook for managing a modal stack with last-opened-first-visible behavior
 * Supports multiple modals of the same type with different IDs
 *
 * @returns {Object} Modal stack management object
 * @returns {Array} modalStack - Current stack of modals
 * @returns {Object|null} visibleModal - The currently visible modal (top of stack)
 * @returns {Function} openModal - Open a modal with (type, id)
 * @returns {Function} closeModal - Close a specific modal by (type, id)
 * @returns {Function} clearAllModals - Clear the entire modal stack
 * @returns {Function} isModalOpen - Check if a specific modal (type, id) is open
 * @returns {Function} isModalVisible - Check if a specific modal (type, id) is currently visible
 */
export const useModalStack = () => {
  const [modalStack, setModalStack] = useState([]);

  // Add a modal to the stack
  const openModal = (type, id) => {
    setModalStack(prevStack => {
      // Remove existing modal with same type AND id to avoid duplicates
      const filtered = prevStack.filter(modal => !(modal.type === type && modal.id === id));
      // Add new modal to top of stack
      return [...filtered, { type, id }];
    });
  };

  // Remove a specific modal from the stack by type and id
  const closeModal = (type, id) => {
    setModalStack(prevStack => prevStack.filter(modal => !(modal.type === type && modal.id === id)));
  };

  // Clear all modals from the stack
  const clearAllModals = () => {
    setModalStack([]);
  };

  // Get the currently visible modal (top of stack)
  const getVisibleModal = () => {
    if (modalStack.length === 0) return null;
    return modalStack[modalStack.length - 1];
  };

  // Check if a specific modal (type, id) is currently in the stack
  const isModalOpen = (type, id) => {
    return modalStack.some(modal => modal.type === type && modal.id === id);
  };

  // Check if a specific modal (type, id) is currently visible (top of stack)
  const isModalVisible = (type, id) => {
    const visibleModal = getVisibleModal();
    return visibleModal?.type === type && visibleModal?.id === id;
  };

  return {
    modalStack,
    visibleModal: getVisibleModal(),
    openModal,
    closeModal,
    clearAllModals,
    isModalOpen,
    isModalVisible,
  };
};
